/**
 * CRM production controls the V3 design leaves out (crm-extras): bulk changes with a
 * 10-second Undo, saved views (save, save as new, reset, share, a default per person) and
 * inline edits on deal rows. Every piece reuses the design's own styles (the catalog's
 * bulk bar and buttons, the deals table's select, the rail's quiet links) and appears only
 * when it has something to act on, so the design's resting states are unchanged.
 *
 * The server does every check again (src/crm/bulk.ts, router.ts): who may change what,
 * the 500-record cap, the stage gates, and the audit row naming the person.
 */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  CRM_BULK_CAP,
  crmBulk,
  crmBulkUndo,
  crmViewCreate,
  crmViewDefault,
  crmViewDelete,
  crmViewUpdate,
  type ApiClient,
  type CrmBulkBody,
  type CrmBulkResponse,
  type CrmMember,
  type CrmSavedView,
  type CrmSmsConsent,
  crmContactSmsConsent,
  crmContactSmsConsentRecord,
} from '@aithora/shared';
import { useSession } from '../../../session/SessionContext';
import { ck, sx } from '../dc';

// ── the design's styles, reused ──
export const BAR = 'display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:5px;padding:6px 10px;margin-bottom:6px;border-radius:9px;background:rgba(79,138,214,.1);';
export const BTN = 'display:inline-flex;align-items:center;height:26px;padding:0 9px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#22272E;border:1px solid rgba(34,39,46,.2);background:#FCFAF4;';
export const BTN_PRIMARY = 'display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#fff;background:#2F5C97;border:1px solid #2F5C97;';
export const SELECT = 'height:26px;box-sizing:border-box;padding:0 6px;border-radius:7px;border:1px solid rgba(34,39,46,.2);background:#FCFAF4;font:inherit;font-size:14px;color:#22272E;';
export const INPUT = 'box-sizing:border-box;height:26px;padding:0 8px;border:1px solid rgba(34,39,46,.18);border-radius:6px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;min-width:0;';
export const LINK = 'font-size:14px;font-weight:600;color:#2F5C97;text-decoration:none;white-space:nowrap;';
export const QUIET = 'font-size:14px;font-weight:600;color:#54606C;text-decoration:none;white-space:nowrap;';
const NOTE = 'font-size:14px;color:#3C454F;min-width:0;';
const ERR = 'font-size:14px;font-weight:700;color:#B3261E;';
const OFF = 'opacity:.5;cursor:not-allowed;';

export const errText = (e: unknown) => (e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'That did not go through.');

/** How long the console offers Undo (the server allows a little more: bulk.ts UNDO_WINDOW_MS). */
export const UNDO_SECONDS = 10;

// ── bulk + undo ───────────────────────────────────────────────────────────────────────

export interface UndoToast {
  text: string;
  opId: string | null;
  until: number;
  error?: boolean;
}

/** Apply a bulk change and keep its Undo for UNDO_SECONDS. */
export function useBulk(client: ApiClient, onChanged: () => void) {
  const [toast, setToast] = useState<UndoToast | null>(null);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!toast?.opId) return;
    const t = setInterval(() => {
      setTick((x) => x + 1);
      if (Date.now() >= toast.until) setToast((cur) => (cur && cur.opId === toast.opId ? { ...cur, opId: null } : cur));
    }, 1000);
    return () => clearInterval(t);
  }, [toast?.opId, toast?.until]);

  const apply = useCallback(
    async (body: CrmBulkBody, done: (r: CrmBulkResponse) => string): Promise<CrmBulkResponse | null> => {
      if (body.ids.length > CRM_BULK_CAP) {
        setToast({ text: `At most ${CRM_BULK_CAP} records at once. Narrow the selection.`, opId: null, until: 0, error: true });
        return null;
      }
      setBusy(true);
      try {
        const r = await crmBulk(client, { body });
        setToast({ text: done(r), opId: r.applied > 0 ? r.opId : null, until: Date.now() + UNDO_SECONDS * 1000 });
        onChanged();
        return r;
      } catch (e) {
        setToast({ text: errText(e), opId: null, until: 0, error: true });
        return null;
      } finally {
        setBusy(false);
      }
    },
    [client, onChanged],
  );

  const undo = useCallback(async () => {
    if (!toast?.opId) return;
    const opId = toast.opId;
    setBusy(true);
    try {
      const r = await crmBulkUndo(client, { params: { opId }, body: {} });
      setToast({ text: `Undone: ${r.reverted} put back` + (r.kept ? `; ${r.kept} changed by someone since, left as they are.` : '.'), opId: null, until: 0 });
      onChanged();
    } catch (e) {
      setToast({ text: errText(e), opId: null, until: 0, error: true });
    } finally {
      setBusy(false);
    }
  }, [client, onChanged, toast?.opId]);

  return { toast, busy, apply, undo, dismiss: () => setToast(null) };
}

/** Summarise a bulk result in one line: what changed, what was already so, what was skipped and why. */
export function bulkLine(r: CrmBulkResponse, what: string, noun: [string, string]): string {
  const n = (x: number) => x + ' ' + (x === 1 ? noun[0] : noun[1]);
  const parts = [`${what} ${n(r.applied)}`];
  if (r.unchanged) parts.push(`${r.unchanged} already so`);
  if (r.skipped) {
    const reasons = [...new Set(r.results.filter((x) => !x.ok).map((x) => x.reason ?? 'refused'))];
    parts.push(`${r.skipped} skipped (${reasons.slice(0, 2).join('; ')})`);
  }
  return parts.join(', ') + '.';
}

export function UndoStrip({ toast, busy, onUndo, onDismiss }: { toast: UndoToast | null; busy: boolean; onUndo: () => void; onDismiss: () => void }) {
  if (!toast) return null;
  const left = toast.opId ? Math.max(0, Math.ceil((toast.until - Date.now()) / 1000)) : 0;
  return (
    <div role="status" data-testid="crm-undo" style={sx(BAR + 'background:rgba(34,39,46,.05);')}>
      <span style={sx(toast.error ? ERR : NOTE)}>{toast.text}</span>
      <span style={sx('flex:1;')} />
      {!!toast.opId && left > 0 && (
        <a href="#" onClick={ck(() => !busy && onUndo())} style={sx(BTN + (busy ? OFF : ''))}>
          Undo ({left}s)
        </a>
      )}
      <a href="#" aria-label="Dismiss" onClick={ck(onDismiss)} style={sx(QUIET)}>
        &#10005;
      </a>
    </div>
  );
}

// ── the bulk action bar's value picker ──────────────────────────────────────────────

export type BulkKind = 'tag_add' | 'tag_remove' | 'owner' | 'status' | 'stage';
export interface BulkOption { v: string; n: string }

/**
 * One bulk action's value step, inline in the bar: a tag box, or a list (owner, status,
 * stage). Enter applies, Esc cancels. `lost` asks for the reason when the stage is Lost.
 */
export function BulkPicker({
  kind,
  options,
  lostIds,
  lostReasons,
  busy,
  onApply,
  onCancel,
}: {
  kind: BulkKind;
  options?: BulkOption[];
  lostIds?: string[];
  lostReasons?: readonly string[];
  busy: boolean;
  onApply: (value: string | null, lostReason?: string) => void;
  onCancel: () => void;
}) {
  const [val, setVal] = useState(options?.[0]?.v ?? '');
  const [reason, setReason] = useState('');
  const ref = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  useEffect(() => ref.current?.focus(), []);
  const isTag = kind === 'tag_add' || kind === 'tag_remove';
  const needsReason = kind === 'stage' && !!lostIds?.includes(val);
  const ok = (isTag ? val.trim().length > 0 && val.trim().length <= 40 : true) && (!needsReason || !!reason) && !busy;
  const go = () => ok && onApply(kind === 'owner' && val === '' ? null : isTag ? val.trim() : val, needsReason ? reason : undefined);
  const keys = (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); go(); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };
  const label = kind === 'tag_add' ? 'Tag to add' : kind === 'tag_remove' ? 'Tag to remove' : kind === 'owner' ? 'New owner' : kind === 'status' ? 'New status' : 'New stage';
  return (
    <span style={sx('display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
      {isTag ? (
        <input ref={(el) => { ref.current = el; }} aria-label={label} placeholder={label} maxLength={40} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={keys} style={sx(INPUT + 'width:150px;')} />
      ) : (
        <select ref={(el) => { ref.current = el; }} aria-label={label} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={keys} style={sx(SELECT)}>
          {(options ?? []).map((o) => (
            <option key={o.v} value={o.v}>{o.n}</option>
          ))}
        </select>
      )}
      {needsReason && (
        <select aria-label="Lost reason" value={reason} onChange={(e) => setReason(e.target.value)} onKeyDown={keys} style={sx(SELECT)}>
          <option value="">Reason…</option>
          {(lostReasons ?? []).map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      )}
      <a href="#" aria-disabled={!ok || undefined} onClick={ck(go)} style={sx(BTN_PRIMARY + (ok ? '' : OFF))}>Apply</a>
      <a href="#" onClick={ck(onCancel)} style={sx(QUIET)}>Cancel</a>
    </span>
  );
}

// ── saved views ───────────────────────────────────────────────────────────────────────

export interface ViewState {
  filters: Record<string, unknown>;
  sort: unknown;
  columns: string[];
}

const canon = (x: unknown): string => {
  if (Array.isArray(x)) return '[' + x.map(canon).join(',') + ']';
  if (x && typeof x === 'object') {
    return '{' + Object.keys(x as Record<string, unknown>).sort()
      .filter((k) => { const v = (x as Record<string, unknown>)[k]; return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0); })
      .map((k) => k + ':' + canon((x as Record<string, unknown>)[k])).join(',') + '}';
  }
  return JSON.stringify(x ?? null);
};
/** Same view? Empty values (undefined, '', [], null) count as unset. */
export const sameView = (a: ViewState, b: ViewState) => canon(a.filters) === canon(b.filters) && canon(a.sort ?? null) === canon(b.sort ?? null) && canon(a.columns) === canon(b.columns);
export const viewStateOf = (v: CrmSavedView): ViewState => ({ filters: v.filters ?? {}, sort: v.sort ?? null, columns: Array.isArray(v.columns) ? v.columns : [] });

/** May this person change this view? (The server decides again: router PATCH /views.) */
export const mayEditView = (v: CrmSavedView, userId: string | null, isManager: boolean) => (v.owner_id && v.owner_id === userId) || (v.is_shared && isManager) || (!v.owner_id && isManager);

/**
 * The saved-view line over a table: shows the active view (Edited when it differs, with
 * Save / Save as new / Reset), sharing, the per-person default and delete; or, with no
 * view active and the table changed, "Save view".
 */
export function ViewStrip({
  client,
  object,
  active,
  current,
  dirty,
  canWrite,
  userId,
  isManager,
  onSaved,
  onReset,
  onClose,
}: {
  client: ApiClient;
  object: 'company' | 'deal';
  active: CrmSavedView | null;
  current: ViewState;
  dirty: boolean;
  canWrite: boolean;
  userId: string | null;
  isManager: boolean;
  onSaved: (v: CrmSavedView | null) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [team, setTeam] = useState(false);
  const [msg, setMsg] = useState<{ t: string; err?: boolean } | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  if (!active && !dirty) return null;
  const editable = !!active && canWrite && mayEditView(active, userId, isManager);
  const owner = !!active && !!userId && active.owner_id === userId;
  const act = async (fn: () => Promise<CrmSavedView | null | void>, ok: string) => {
    setMsg(null);
    try {
      const v = await fn();
      onSaved(v ?? null);
      setMsg({ t: ok });
    } catch (e) {
      setMsg({ t: errText(e), err: true });
    }
  };
  const create = () => {
    const nm = name.trim();
    if (!nm || !canWrite) return;
    void act(async () => {
      const r = await crmViewCreate(client, { body: { object, name: nm, filters: current.filters, sort: current.sort ?? undefined, columns: current.columns, scope: team ? 'Team' : 'Mine' } });
      setNaming(false);
      setName('');
      return r.view;
    }, `Saved "${nm}"` + (team ? ' for the team.' : ' for you.'));
  };
  const nameKeys = (e: KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); create(); }
    if (e.key === 'Escape') { e.preventDefault(); setNaming(false); }
  };
  return (
    <div data-testid={`crm-viewstrip-${object}`} style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:4px;padding:0 0 6px;')}>
      {active ? (
        <>
          <span style={sx('font-size:16px;font-weight:700;color:#22272E;')} translate="no">{active.name}</span>
          <span style={sx('font-size:14px;color:#54606C;')}>
            {active.is_shared ? 'Team' : 'Mine'}
            {active.is_default ? ' · your default' : ''}
            {active.is_shared && active.owner_name && !owner ? ' · by ' + active.owner_name : ''}
          </span>
          {dirty && <span style={sx('font-size:14px;font-weight:700;color:#8A5A00;')}>Edited</span>}
          {dirty && editable && (
            <a href="#" onClick={ck(() => void act(async () => (await crmViewUpdate(client, { params: { id: active.id }, body: { filters: current.filters, sort: current.sort, columns: current.columns } })).view, 'Saved.'))} style={sx(LINK)}>Save</a>
          )}
          {dirty && canWrite && <a href="#" onClick={ck(() => { setNaming(true); setTeam(false); setName(active.name + ' (copy)'); })} style={sx(LINK)}>Save as new</a>}
          {dirty && <a href="#" onClick={ck(onReset)} style={sx(QUIET)}>Reset</a>}
          {!!userId && canWrite && (
            <a href="#" onClick={ck(() => void act(async () => { await crmViewDefault(client, { body: { object, viewId: active.is_default ? null : active.id } }); return { ...active, is_default: !active.is_default }; }, active.is_default ? 'No default view now.' : 'This view opens first for you now.'))} style={sx(QUIET)}>
              {active.is_default ? 'Not my default' : 'Make my default'}
            </a>
          )}
          {editable && (owner || isManager) && (
            <a href="#" onClick={ck(() => void act(async () => (await crmViewUpdate(client, { params: { id: active.id }, body: { scope: active.is_shared ? 'Mine' : 'Team' } })).view, active.is_shared ? 'Only you see it now.' : 'The team sees it now.'))} style={sx(QUIET)}>
              {active.is_shared ? 'Make private' : 'Share with team'}
            </a>
          )}
          {editable && !confirmDel && <a href="#" onClick={ck(() => setConfirmDel(true))} style={sx(QUIET)}>Delete</a>}
          {editable && confirmDel && (
            <>
              <span style={sx('font-size:14px;color:#B3261E;')}>{active.is_shared ? 'Delete it for the whole team?' : 'Delete this view?'}</span>
              <a href="#" onClick={ck(() => void act(async () => { await crmViewDelete(client, { params: { id: active.id } }); setConfirmDel(false); onClose(); return null; }, 'Deleted.'))} style={sx(LINK + 'color:#B3261E;')}>Delete</a>
              <a href="#" onClick={ck(() => setConfirmDel(false))} style={sx(QUIET)}>Keep</a>
            </>
          )}
          <a href="#" aria-label="Close the view" onClick={ck(onClose)} style={sx(QUIET)}>&#10005;</a>
        </>
      ) : (
        <>
          {canWrite && !naming && <a href="#" onClick={ck(() => setNaming(true))} style={sx(LINK)}>Save as a view</a>}
          <a href="#" onClick={ck(onReset)} style={sx(QUIET)}>Reset</a>
        </>
      )}
      {naming && (
        <span style={sx('display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap;')}>
          <input autoFocus aria-label="View name" placeholder="View name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={nameKeys} style={sx(INPUT + 'width:170px;')} />
          <select aria-label="Who sees it" value={team ? 'Team' : 'Mine'} onChange={(e) => setTeam(e.target.value === 'Team')} style={sx(SELECT)}>
            <option value="Mine">Mine</option>
            <option value="Team">Team</option>
          </select>
          <a href="#" aria-disabled={!name.trim() || undefined} onClick={ck(create)} style={sx(BTN_PRIMARY + (name.trim() ? '' : OFF))}>Save</a>
          <a href="#" onClick={ck(() => setNaming(false))} style={sx(QUIET)}>Cancel</a>
        </span>
      )}
      {msg && <span role="status" style={sx(msg.err ? ERR : 'font-size:14px;color:#54606C;')}>{msg.t}</span>}
    </div>
  );
}

// ── inline edit on a deal row ────────────────────────────────────────────────────────

/**
 * Click (or Enter) to edit; Enter or leaving the field saves, Esc cancels. The new value
 * shows at once; the caller rolls it back if the server refuses (useCrm's inline edit).
 */
export function InlineCell({
  display,
  value,
  type,
  label,
  editable,
  title,
  style,
  onCommit,
}: {
  display: string;
  value: string;
  type: 'number' | 'date';
  label: string;
  editable: boolean;
  title?: string;
  style: string;
  onCommit: (raw: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const done = useRef(false);
  if (!editable) {
    return (
      <span style={sx(style)} title={title}>
        <span>{display}</span>
      </span>
    );
  }
  if (!editing) {
    return (
      <span
        role="button"
        tabIndex={0}
        aria-label={`${label}: ${display}. Press Enter to edit.`}
        onClick={(e) => { e.stopPropagation(); done.current = false; setDraft(value); setEditing(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); e.stopPropagation(); done.current = false; setDraft(value); setEditing(true); } }}
        style={sx(style + 'cursor:text;')}
        title="Click to edit"
      >
        <span>{display}</span>
      </span>
    );
  }
  const commit = () => {
    if (done.current) return;
    done.current = true;
    setEditing(false);
    if (draft !== value) onCommit(draft);
  };
  return (
    <span onClick={(e) => e.stopPropagation()} style={sx('min-width:0;')}>
      <input
        autoFocus
        type={type}
        aria-label={label}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? 1000 : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); done.current = true; setEditing(false); }
        }}
        style={sx(INPUT + 'width:100%;height:24px;padding:0 5px;font-size:14px;')}
      />
    </span>
  );
}

/** The owner cell as a quiet select: a manager assigns anyone; a rep may take an unowned deal. */
export function OwnerCell({
  display,
  ownerId,
  members,
  editable,
  claimOnly,
  userId,
  style,
  onChange,
}: {
  display: string;
  ownerId: string | null;
  members: CrmMember[];
  editable: boolean;
  claimOnly: boolean;
  userId: string | null;
  style: string;
  onChange: (id: string | null) => void;
}) {
  if (!editable) {
    return (
      <span style={sx(style)}>
        <span>{display}</span>
      </span>
    );
  }
  const opts = claimOnly ? members.filter((m) => m.id === userId) : members;
  return (
    <select
      aria-label="Owner"
      value={ownerId ?? ''}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.value || null)}
      style={sx(style + 'appearance:none;-webkit-appearance:none;border:0;background:transparent;font:inherit;font-size:16px;padding:0;cursor:pointer;min-width:0;width:100%;text-overflow:ellipsis;')}
      title={claimOnly ? 'Take this deal' : 'Change the owner'}
    >
      {(!claimOnly || !ownerId) && <option value="">Unassigned</option>}
      {opts.map((m) => (
        <option key={m.id} value={m.id}>{(m.name ?? m.email ?? '').split(/[\s@]/)[0] || 'A teammate'}</option>
      ))}
    </select>
  );
}

// ── Sales-text consent on the contact card (TCPA; src/aithora/contact-consent.ts) ──
// No CRM text goes to a number without recorded consent to sales texts, and never after a
// STOP (the server refuses with a 409 that says so). The rep records the consent the
// prospect gave, how (verbal, paper or a web form) and the exact words, for the number on
// the contact; the server audits it. Not in the V3 design: one quiet block under Identity.

const SOURCES: Array<{ v: 'verbal' | 'paper' | 'web_form'; n: string }> = [
  { v: 'verbal', n: 'Verbal' },
  { v: 'paper', n: 'Paper form' },
  { v: 'web_form', n: 'Web form' },
];
const HOW: Record<string, string> = { verbal: 'verbal', paper: 'paper form', web_form: 'web form', sms_keyword: 'texted START' };

/** One line on the sales-text consent on file. */
export function smsConsentLine(c: CrmSmsConsent): string {
  if (!c.phone) return 'No phone on this contact, so there is nothing to text.';
  if (c.optedOut) return 'This number replied STOP. No texts go to it; only the person can turn them back on, by texting START.';
  if (!c.consent.onFile) return 'No consent to sales texts on file, so no text can be sent to this number.';
  const when = c.consent.recordedAt ? new Date(c.consent.recordedAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const by = c.consent.recordedByName ? ', recorded by ' + c.consent.recordedByName : '';
  return 'Agreed to sales texts (' + (HOW[c.consent.source ?? ''] ?? c.consent.source ?? '') + (when ? ', ' + when : '') + by + '). STOP withdraws it.';
}

export function ContactSmsConsent({ contactId, phoneKey, writable, writeTitle }: { contactId: string; phoneKey: string; writable: boolean; writeTitle?: string }) {
  const { client } = useSession();
  const [c, setC] = useState<CrmSmsConsent | null>(null);
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<'verbal' | 'paper' | 'web_form'>('verbal');
  const [wording, setWording] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setC(null);
    setOpen(false);
    crmContactSmsConsent(client, { params: { id: contactId } })
      .then((r) => { if (live) { setC(r); setWording(r.wording); } })
      .catch((e) => { if (live) setMsg(errText(e)); });
    return () => { live = false; };
  }, [client, contactId, phoneKey]);
  if (!c) return msg ? <div role="status" style={sx('font-size:14px;color:#3C454F;margin-top:9px;')}>{msg}</div> : null;
  const off = !writable;
  const send = async (granted: boolean) => {
    if (off) return;
    setMsg(null);
    try {
      const r = await crmContactSmsConsentRecord(client, { params: { id: contactId }, body: { source, wording: wording.trim(), granted, note: note.trim() || undefined } });
      setC(r);
      setOpen(false);
      setNote('');
      setMsg(granted ? 'Consent recorded with your name and the time.' : 'Withdrawal recorded. No texts go to this number now.');
    } catch (e) {
      setMsg(errText(e));
    }
  };
  const offStyle = off ? 'color:#54606C;pointer-events:none;' : '';
  return (
    <div data-testid="crm-sms-consent" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 14px;margin-top:10px;display:flex;flex-direction:column;gap:6px;')}>
      <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Sales texts</span>
      <span style={sx('font-size:14px;color:#3C454F;text-wrap:pretty;')}>{smsConsentLine(c)}</span>
      {c.phone && !c.optedOut && !open && (
        <div style={sx('display:flex;gap:12px;flex-wrap:wrap;')}>
          {c.consent.onFile ? (
            <a href="#" aria-disabled={off || undefined} title={off ? writeTitle : undefined} onClick={ck(() => void send(false))} style={sx(LINK + offStyle)}>
              Record withdrawal
            </a>
          ) : (
            <a href="#" aria-disabled={off || undefined} title={off ? writeTitle : undefined} onClick={ck(() => { if (!off) setOpen(true); })} style={sx(LINK + offStyle)}>
              Record the consent they gave
            </a>
          )}
        </div>
      )}
      {open && (
        <div style={sx('display:flex;flex-direction:column;gap:6px;')}>
          <div style={sx('display:flex;gap:6px;flex-wrap:wrap;align-items:center;')}>
            <span style={sx('font-size:14px;color:#54606C;')}>How they gave it</span>
            <select aria-label="How they gave consent" value={source} onChange={(e) => setSource(e.target.value as typeof source)} style={sx(SELECT)}>
              {SOURCES.map((s) => <option key={s.v} value={s.v}>{s.n}</option>)}
            </select>
          </div>
          <label style={sx('font-size:14px;color:#54606C;display:flex;flex-direction:column;gap:4px;')}>
            The exact words they agreed to
            <textarea value={wording} onChange={(e) => setWording(e.target.value)} rows={4} style={sx('box-sizing:border-box;padding:6px 8px;border:1px solid rgba(34,39,46,.18);border-radius:6px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;resize:vertical;')} />
          </label>
          <input aria-label="Note (optional)" placeholder="Note, for example the form's reference (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={sx(INPUT)} />
          <div style={sx('display:flex;gap:12px;flex-wrap:wrap;')}>
            <a href="#" aria-disabled={!wording.trim() || undefined} onClick={ck(() => { if (wording.trim()) void send(true); })} style={sx(LINK + (wording.trim() ? '' : 'color:#54606C;pointer-events:none;'))}>
              Record consent
            </a>
            <a href="#" onClick={ck(() => setOpen(false))} style={sx(QUIET)}>Cancel</a>
          </div>
        </div>
      )}
      {msg && <span role="status" style={sx('font-size:14px;color:#3C454F;')}>{msg}</span>}
    </div>
  );
}
